import { useDisclosure } from "@shared/hooks/useDisclosure";
import { useSeatingThemeSync } from "@shared/hooks/seating/useSeatingThemeSync";
import { useEffect } from "react";
import { useTranslation } from "@shared/utils/internationalization";
import PageSpinner from "@ui-kit/feedback/PageSpinner/PageSpinner.web";
import Modal from "@ui-kit/overlays/Modal/Modal.web";
import { useSeatingState } from "../../contexts/useSeatingState";
import LegendContent from "../LegendContent/LegendContent.web";
import MapButtons from "../MapButtons/MapButtons.web";
import MapCanvas from "../MapCanvas/MapCanvas.web";
import MapMessage from "../MapMessage/MapMessage.web";
import MapTimeslotDrawer from "../MapTimeslotDrawer/MapTimeslotDrawer.web";
import MapTooltip from "../MapTooltip/MapTooltip.web";
import { BasicSeatingMapSharedProps } from "./BasicSeatingMap.props";
import { useBasicSeatingMap } from "./useBasicSeatingMap";

const ZOOM_VAL = 0.25;

/**
 * This component handles basic sales map functionality. It displays:
 * loading spinners, the map, the legend modal, tooltips, and zoom buttons.
 *
 * It currently handles logic for box office maps and custom package maps (public & box office).
 *
 * It DOES NOT handle group sales maps.
 * It DOES NOT handle item assigner map logic.
 */
export default function BasicSeatingMap(props: BasicSeatingMapSharedProps) {
  const { t } = useTranslation("seating");

  const {
    venue,
    mapItemType,
    hideDrawer,
    isBestAvailableListOpen,
    onToggleBestAvailableList,
  } = props;

  const {
    isUpdatingMap,
    isLoadingMap, // displays loading spinner when loading or reloading the map
    stageServiceState,
    seatingServiceState,
    seatingEventData,
    seatingItemArray,
  } = useSeatingState();

  useSeatingThemeSync(stageServiceState);

  // seating legend modal variables (opens from the info icon)
  const {
    isOpen: isLegendModalOpen,
    onOpen: onLegendModalOpen,
    onClose: onLegendModalClose,
  } = useDisclosure({});

  const {
    displaySeatingMessage,
    tooltipInfo,
    mousePosition,
    showTooltip,
    canvasId,
    allInPricing,
    advancedSeatColors,
    parentEventData,
    showTimeslotDrawer,
    isPackageFlow,
  } = useBasicSeatingMap(props);

  useEffect(() => {
    if (stageServiceState) {
      stageServiceState.setBestAvailableListOpen(
        Boolean(isBestAvailableListOpen)
      );
    }
  }, [stageServiceState, isBestAvailableListOpen]);

  return (
    <>
      {/* Display spinner while updating or loading map selections */}
      {(isUpdatingMap || isLoadingMap) && (
        <PageSpinner
          lowOpacity={isUpdatingMap && !isLoadingMap}
          spinnerText={isLoadingMap ? t("Loading Seating Map") : undefined}
        />
      )}
      {!isLoadingMap && (
        <>
          {/* Only show text overlay when everything is loaded */}
          <MapMessage
            displayMessage={displaySeatingMessage()}
            messageText={t("Select a section to view available items")}
          />
          {/* Only show map buttons when everything is loaded */}
          <MapButtons
            centerStage={stageServiceState?.centerStage}
            zoomIn={() => stageServiceState?.zoomBy(ZOOM_VAL)}
            zoomOut={() => stageServiceState?.zoomBy(-ZOOM_VAL)}
            onLegendModalOpen={onLegendModalOpen}
            isBestAvailableListOpen={isBestAvailableListOpen}
            onToggleBestAvailableList={onToggleBestAvailableList}
          />
          {/* Only show timeslot drawer when everything is loaded AND map is public AND not showOtherEvents AND child events exist */}
          {!hideDrawer && showTimeslotDrawer && (
            <MapTimeslotDrawer
              childEvents={parentEventData?.child_events ?? []}
              currentEventId={seatingEventData?.id}
            />
          )}
          {/* Only show map tooltips when everything is loaded */}
          {tooltipInfo && (
            <MapTooltip
              currency={venue.currency}
              mousePosition={mousePosition}
              visible={showTooltip}
              info={tooltipInfo}
              canvasId={canvasId}
              allInPricing={allInPricing}
              hideMinPurchaseText={
                seatingServiceState?.overridePurchaseLimits?.multiItemsAllowed
              }
              basketType={seatingServiceState.basketType}
              hidePrice={isPackageFlow}
            />
          )}
          {/* Only show legend when everything is loaded */}
          <Modal
            isOpen={isLegendModalOpen}
            onClose={onLegendModalClose}
            scrollBehavior="inside"
            size="3xl"
          >
            <Modal.Header>{t("Legend")}</Modal.Header>
            <Modal.Body>
              <LegendContent
                currency={venue.currency}
                advancedColors={advancedSeatColors}
                itemTypes={seatingItemArray ?? []}
                mapItemType={mapItemType}
                allInPricing={allInPricing}
                hideMinPurchaseText={
                  seatingServiceState?.overridePurchaseLimits?.multiItemsAllowed
                }
                basketType={seatingServiceState.basketType}
                hidePrice={isPackageFlow}
              />
            </Modal.Body>
          </Modal>
        </>
      )}
      {/* Interactive map canvas */}
      <MapCanvas isLoading={isLoadingMap} canvasId={canvasId} />
    </>
  );
}
